import {useMemo} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible as isInaccessibleGQL} from 'graphql-client/InaccessibleInfo';
import APIUtil from 'util/APIUtil';

import {SourceSampleSelectionTableQuery} from './sample-selection/__generated/SourceSampleSelectionTableQuery';
import {SOURCE_SAMPLE_SELECTION_TABLE_QUERY} from './sample-selection/useSampleQueryData';

/**
 * This hook is used by the AddSamplesFromWorklistModal & AddSamplesFromBarcodeScannerModal components.
 * It hydrates the sample data, but does not return them. (They simply need to be available in the store.)
 *
 * If the query has been successfully completed, we also return a mapping of {sampleId: schemaId} and a
 * mapping of {sampleId: parentInterfaceIds}. The latter lets callers match samples against a destination
 * that requires a schema interface (fieldset) rather than a concrete schema — a sample is compatible when
 * one of its schema's parent interfaces matches the destination.
 */

export default function useHydrateWorklistSamples(sampleIds: Array<string>): {
  status: QueryResultType;
  schemaIdBySampleId: {[sampleId: string]: string};
  parentInterfaceIdsBySampleId: {[sampleId: string]: Array<string>};
} {
  const queryResult = useBenchlingQuery<SourceSampleSelectionTableQuery>(
    SOURCE_SAMPLE_SELECTION_TABLE_QUERY,
    {
      variables: {
        bioentityIds: sampleIds.filter((id) => APIUtil.isBiologicalEntity(id)),
        boxIds: [],
        containerIds: sampleIds.filter((id) => APIUtil.isContainer(id)),
        plateIds: sampleIds.filter((id) => APIUtil.isPlate(id)),
      },
    }
  );
  const queryData = queryResult.type === QueryResultType.SUCCESS ? queryResult.data : null;
  const schemaIdBySampleId = useMemo<{[sampleId: string]: string}>(() => {
    const newSchemaIdBySampleId: {[sampleId: string]: string} = {};
    if (queryData == null) {
      return newSchemaIdBySampleId;
    }

    for (const bioentity of queryData.bioentities) {
      if (!isInaccessibleGQL(bioentity)) {
        const sample = graphQLToREST('BioentitySummary', bioentity);
        const schemaId = sample.tagSchema?.id;
        if (schemaId != null) {
          newSchemaIdBySampleId[sample.id] = schemaId;
        }
      }
    }

    for (const container of queryData.containers) {
      if (!isInaccessibleGQL(container)) {
        const sample = graphQLToREST('Container', container);
        newSchemaIdBySampleId[sample.id] = sample.storableSchema.id;
      }
    }

    for (const plate of queryData.plates) {
      if (!isInaccessibleGQL(plate)) {
        const sample = graphQLToREST('PlateSummary', plate);
        newSchemaIdBySampleId[sample.id] = sample.storableSchema.id;
      }
    }

    return newSchemaIdBySampleId;
  }, [queryData]);

  const parentInterfaceIdsBySampleId = useMemo<{[sampleId: string]: Array<string>}>(() => {
    const newParentInterfaceIdsBySampleId: {[sampleId: string]: Array<string>} = {};
    if (queryData == null) {
      return newParentInterfaceIdsBySampleId;
    }

    for (const bioentity of queryData.bioentities) {
      if (!isInaccessibleGQL(bioentity) && bioentity.tagSchema != null) {
        const interfaceIds = bioentity.tagSchema.interfaces.map((parent) => parent.id);
        if (interfaceIds.length > 0) {
          newParentInterfaceIdsBySampleId[bioentity.id] = interfaceIds;
        }
      }
    }

    return newParentInterfaceIdsBySampleId;
  }, [queryData]);

  return {status: queryResult.type, schemaIdBySampleId, parentInterfaceIdsBySampleId};
}
